/**
 * Construction-fragment CAPTURE (#763) — paper-free on purpose, so the
 * clipboard's copy half is unit-testable in jsdom. The paste half
 * (`insertConstructionFragment`) lives in `constructionGroupActions`, whose
 * transform helpers pull in paper.
 */
import {
  descendantConstructionGroupIds,
  fullySelectedConstructionGroupIds,
  groupAncestorIds,
  projectConstructionGroups,
  projectConstructionLines,
  projectTracingImages,
  type Constraint,
  type ConstructionGroup,
  type ConstructionLine,
  type Project,
  type TracingImage,
} from "./Project";
import { descendantLeafIds } from "./ConstructionObject";
import { projectConstraints } from "./constraints";

/** A self-contained construction FRAGMENT: full model snapshots of the
 *  lines / images / groups to clone (in project display order), the
 *  constraints internal to the set, and the selection shape that produced
 *  it (which groups were the top-level selected entities, which lines /
 *  images stood alone). Captured by {@link captureConstructionFragment},
 *  consumed by {@link insertConstructionFragment} — immediately for
 *  Cmd+D, or later as the app clipboard's paste payload (#763). It holds
 *  OBJECTS rather than ids on purpose: the project's immutable updates
 *  mean the snapshot stays valid even after the originals are edited or
 *  deleted. */
export interface ConstructionFragment {
  lines: ConstructionLine[];
  images: TracingImage[];
  groups: ConstructionGroup[];
  constraints: Constraint[];
  topLevelGroupIds: string[];
  standaloneLineIds: string[];
  standaloneImageIds: string[];
}

/** Resolve a construction selection into a {@link ConstructionFragment}.
 *  A group whose entire descendant member set is selected rides along as
 *  an entity (auto-promotion, mirroring Cmd+G); lines / images inside a
 *  selected group travel with the group rather than getting a second
 *  standalone copy; only constraints fully INTERNAL to the set are kept
 *  — a copy pinned to a non-copied line would tug its source. Returns
 *  null for an empty selection. */
export function captureConstructionFragment(
  proj: Project,
  sel: {
    constructionIds?: string[];
    constructionGroupIds?: string[];
    tracingImageIds?: string[];
  },
): ConstructionFragment | null {
  const selectedLineIds = sel.constructionIds ?? [];
  const selectedImageIds = sel.tracingImageIds ?? [];
  const selectedGroupIds = sel.constructionGroupIds ?? [];
  if (
    selectedLineIds.length === 0 &&
    selectedImageIds.length === 0 &&
    selectedGroupIds.length === 0
  ) {
    return null;
  }
  // Auto-promote: a group whose entire descendant member set (lines AND
  // images) is selected rides along as an entity — mirrors Cmd+G
  // semantics so duplicating "every member of this group" actually
  // clones the group, not just bare copies.
  const promoted = fullySelectedConstructionGroupIds(
    proj,
    selectedLineIds,
    selectedImageIds,
  );
  const allSelectedGroupIds = Array.from(
    new Set([...selectedGroupIds, ...promoted]),
  );
  // Top-level among the selected groups — descendants of another
  // selected group ride along inside their ancestor's clone instead
  // of being duplicated twice.
  const topLevelSelectedGroups = allSelectedGroupIds.filter((gid) => {
    const ancestors = groupAncestorIds(proj, gid);
    return !ancestors.some((aid) => allSelectedGroupIds.includes(aid));
  });
  // Lines / images that sit inside any selected group ride along with
  // the group's duplicate, so they don't get a second standalone copy.
  const linesOwnedBySelectedGroups = new Set<string>();
  const imagesOwnedBySelectedGroups = new Set<string>();
  for (const gid of topLevelSelectedGroups) {
    const members = descendantLeafIds(proj, gid);
    for (const lid of members.lineIds) linesOwnedBySelectedGroups.add(lid);
    for (const iid of members.imageIds) imagesOwnedBySelectedGroups.add(iid);
  }
  const standaloneLineIds = selectedLineIds.filter(
    (id) => !linesOwnedBySelectedGroups.has(id),
  );
  const standaloneImageIds = selectedImageIds.filter(
    (id) => !imagesOwnedBySelectedGroups.has(id),
  );
  // Resolve the full duplication set: every standalone line / image
  // plus every descendant of every duplicated group. Same logic for
  // groups (the duplicated group's subtree).
  const allLinesToDuplicate = new Set<string>(standaloneLineIds);
  const allImagesToDuplicate = new Set<string>(standaloneImageIds);
  for (const gid of topLevelSelectedGroups) {
    const members = descendantLeafIds(proj, gid);
    for (const lid of members.lineIds) allLinesToDuplicate.add(lid);
    for (const iid of members.imageIds) allImagesToDuplicate.add(iid);
  }
  const allGroupsToDuplicate = new Set<string>();
  for (const gid of topLevelSelectedGroups) {
    for (const dgid of descendantConstructionGroupIds(proj, gid)) {
      allGroupsToDuplicate.add(dgid);
    }
  }
  if (
    allLinesToDuplicate.size === 0 &&
    allImagesToDuplicate.size === 0 &&
    allGroupsToDuplicate.size === 0
  ) {
    return null;
  }
  const lines = projectConstructionLines(proj).filter((l) =>
    allLinesToDuplicate.has(l.id),
  );
  const images = projectTracingImages(proj).filter((i) =>
    allImagesToDuplicate.has(i.id),
  );
  const groups = projectConstructionGroups(proj).filter((g) =>
    allGroupsToDuplicate.has(g.id),
  );
  const constraints = projectConstraints(proj).filter((c) =>
    c.refs.every((r) => allLinesToDuplicate.has(r.constructionId)),
  );
  return {
    lines,
    images,
    groups,
    constraints,
    topLevelGroupIds: topLevelSelectedGroups,
    standaloneLineIds,
    standaloneImageIds,
  };
}
